import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { products, provinces } from '../data/mock';
import StepNavigation from '../components/StepNavigation';
import Step1ContactAndMap from '../components/Step1ContactAndMap';
import Step2KitSelection from '../components/Step2KitSelection';
import Step3Result from '../components/Step3Result';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PromoVideo from '../components/PromoVideo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GeyserProLanding = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [contactData, setContactData] = useState(null);
  const [gardenData, setGardenData] = useState(null);
  const [selectedKit, setSelectedKit] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [routingData, setRoutingData] = useState(null);

  const gardenSize = gardenData?.areaM2 || 0;

  const { recommendedKit, upsellKit } = useMemo(() => {
    if (!gardenSize) return { recommendedKit: null, upsellKit: null };
    const sortedProducts = [...products].sort((a, b) => a.coverage - b.coverage);
    const recommendedIndex = sortedProducts.findIndex(p => p.coverage >= gardenSize);
    if (recommendedIndex === -1) {
      return { recommendedKit: sortedProducts[sortedProducts.length - 1], upsellKit: null };
    }
    const recommended = sortedProducts[recommendedIndex];
    const upsell = recommendedIndex < sortedProducts.length - 1 ? sortedProducts[recommendedIndex + 1] : null;
    return { recommendedKit: recommended, upsellKit: upsell };
  }, [gardenSize]);

  // Step 1: Contact + Garden → create lead → go to Step 2
  const handleStep1Submit = async (data) => {
    setContactData(data.contactData);
    setGardenData(data.gardenData);
    setIsLoading(true);
    try {
      if (window.fbq) window.fbq('track', 'Lead');
      const response = await axios.post(`${API_URL}/api/leads/create`, {
        first_name: data.contactData.firstName,
        last_name: data.contactData.lastName,
        email: data.contactData.email,
        phone: data.contactData.phone,
        provincia: data.contactData.province,
        city: data.contactData.city || null,
        postcode: data.contactData.postcode,
        input_mode: data.gardenData.inputMode === 'manual' ? 'manuale' : 'mappa',
        area_m2: data.gardenData.areaM2,
        perimeter_m: data.gardenData.perimeterM || null,
        address_query: data.gardenData.addressQuery || null,
        pin_lat: data.gardenData.pinLat || null,
        pin_lng: data.gardenData.pinLng || null,
        map_drawing_data: data.gardenData.mapDrawingData || null,
        consent_marketing: !!data.consents?.marketing
      });
      setLeadId(response.data.lead_id);
      setCurrentStep(2);
    } catch {
      // Lead creation failed — proceed anyway so the user can still select a kit;
      // the submit step will recreate/handle the lead via backend.
      setCurrentStep(2);
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Kit selected → update lead + submit → go DIRECTLY to result
  const handleStep2Submit = async (kit) => {
    setSelectedKit(kit);
    setIsLoading(true);
    try {
      if (leadId) {
        // Update lead with kit selection
        await axios.put(`${API_URL}/api/leads/update-step2`, {
          lead_id: leadId,
          kits_shown: products.map(p => ({ id: p.id, name: p.name })),
          kit_selected_id: kit.id,
          kit_selected_name: kit.name,
          kit_selected_coverage: kit.coverage,
          kit_selected_price: kit.price,
          kit_consigliato: kit.id === recommendedKit?.id
        });

        // Immediately submit for routing
        if (window.fbq) window.fbq('track', 'CompleteRegistration');
        const response = await axios.post(`${API_URL}/api/leads/submit`, {
          lead_id: leadId
        });
        setRoutingData(response.data);
      }
    } catch {
      // Submit failed — user will see Step3 but without routing data.
      // Step3Result handles null routingData with a generic fallback message.
    } finally {
      setIsLoading(false);
    }
    setCurrentStep(3);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 md:py-12">
        <StepNavigation 
          currentStep={currentStep} 
          onStepClick={(step) => { if (step < currentStep) setCurrentStep(step); }}
        />

        {currentStep === 1 && (
          <div className="text-center mt-8 md:mt-10 mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 uppercase tracking-wide">
              Disegna il tuo giardino e trova la protezione perfetta
            </h1>
            <h2 className="text-base md:text-lg font-bold text-gray-700 mt-2 uppercase tracking-wide">
              Finalmente senza zanzare
            </h2>
            <p className="text-sm md:text-base text-gray-500 mt-1">
              Ricevi subito un preventivo personalizzato da una rivendita specializzata.
            </p>
          </div>
        )}

        {/* Sticky video - only Step 1 */}
        {currentStep === 1 && <PromoVideo />}
        
        <div>
          {currentStep === 1 && (
            <Step1ContactAndMap 
              provinces={provinces}
              onSubmit={handleStep1Submit}
              initialData={{ contactData, gardenData }}
            />
          )}
          
          {currentStep === 2 && (
            <Step2KitSelection
              products={products}
              gardenSize={gardenSize}
              recommendedKit={recommendedKit}
              upsellKit={upsellKit}
              selectedKit={selectedKit}
              onSubmit={handleStep2Submit}
              onBack={() => setCurrentStep(1)}
              isLoading={isLoading}
            />
          )}
          
          {currentStep === 3 && (
            <Step3Result
              selectedKit={selectedKit}
              gardenSize={gardenSize}
              routingData={routingData}
              onBack={() => setCurrentStep(2)}
              leadId={leadId}
            />
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default GeyserProLanding;
